import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useOrganizationQuery,
  useRefreshCustomersMutation,
  useRefreshInvoicesMutation,
} from "../api/queries/organizationQueries";
import type { Organization } from "../utils/types";
import {
  organizationKeys,
  paymentKeys,
  customerRepresentativeKeys,
} from "../api/queries/queryKeys";
import { receivablesKeys } from "../api/queries/receivablesQueries";
import {
  useInflightProcessingTask,
  useProcessingServiceTaskPolling,
} from "../hooks/useProcessingServiceTaskPolling";

const TIMEOUT_MS = 15 * 60 * 1000;

export type AccountingRefreshEnqueueStatus =
  | "queued"
  | "already_running"
  | "backpressure";

type AccountingRefreshContextValue = {
  organization: Organization | undefined;
  refreshInvoicesMutation: ReturnType<typeof useRefreshInvoicesMutation>;
  refreshCustomersMutation: ReturnType<typeof useRefreshCustomersMutation>;
  isRefreshing: boolean;
  startRefresh: (options?: { customersOnly?: boolean }) => void;
};

const AccountingRefreshContext =
  createContext<AccountingRefreshContextValue | null>(null);

export function AccountingRefreshProvider({
  children,
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const refreshInvoicesMutation = useRefreshInvoicesMutation();
  const refreshCustomersMutation = useRefreshCustomersMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const customersOnlyRef = useRef(false);
  const completionHandledRef = useRef(false);

  const inflightQuery = useInflightProcessingTask("refresh_invoices", {
    enabled: !isRefreshing,
  });

  const taskQuery = useProcessingServiceTaskPolling(jobId);

  const { data: organization } = useOrganizationQuery();

  const refetchInvoiceQueries = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: paymentKeys.all, type: "all" }),
      queryClient.refetchQueries({ queryKey: receivablesKeys.all, type: "all" }),
      queryClient.refetchQueries({
        queryKey: customerRepresentativeKeys.all,
        type: "all",
      }),
    ]);
  }, [queryClient]);

  const finishRefresh = useCallback(
    async (
      outcome: "success" | "timeout" | "failed",
      errorMessage?: string,
    ) => {
      if (completionHandledRef.current) return;
      completionHandledRef.current = true;
      setIsRefreshing(false);
      setJobId(null);

      if (outcome === "success") {
        await Promise.all([
          refetchInvoiceQueries(),
          queryClient.refetchQueries({ queryKey: organizationKeys.all }),
        ]);
        toast.success("Refresh completed", {
          description: customersOnlyRef.current
            ? "Customer contacts have been updated from your accounting software."
            : "Invoice data has been updated from your accounting software.",
        });
      } else if (outcome === "failed") {
        toast.error("Refresh failed", {
          description:
            errorMessage ||
            "Could not refresh invoices from your accounting software.",
        });
      } else {
        await refetchInvoiceQueries();
        toast.warning("Refresh still in progress", {
          description:
            "This can take several minutes. Check back later or refresh the page.",
        });
      }
    },
    [queryClient, refetchInvoiceQueries],
  );

  // Resume inflight task after page reload
  useEffect(() => {
    const inflightTask = inflightQuery.data;
    if (!inflightTask || isRefreshing) return;
    if (
      inflightTask.status === "pending" ||
      inflightTask.status === "running"
    ) {
      completionHandledRef.current = false;
      setJobId(inflightTask.job_id);
      setIsRefreshing(true);
    }
  }, [inflightQuery.data, isRefreshing]);

  // React to task status changes from polling
  useEffect(() => {
    if (!isRefreshing || !taskQuery.data) return;

    const { status, error_message } = taskQuery.data;

    if (status === "completed") {
      void finishRefresh("success");
    } else if (status === "failed") {
      void finishRefresh("failed", error_message ?? undefined);
    }
  }, [isRefreshing, taskQuery.data, finishRefresh]);

  // 15-min client-side timeout backup
  useEffect(() => {
    if (!isRefreshing) return;
    const timer = window.setTimeout(() => {
      void finishRefresh("timeout");
    }, TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isRefreshing, finishRefresh]);

  const startRefresh = useCallback(
    (options?: { customersOnly?: boolean }) => {
      const customersOnly = options?.customersOnly ?? false;
      customersOnlyRef.current = customersOnly;
      completionHandledRef.current = false;

      const mutation = customersOnly
        ? refreshCustomersMutation
        : refreshInvoicesMutation;

      mutation.mutate(undefined, {
        onSuccess: (data) => {
          const status = data?.status as
            | AccountingRefreshEnqueueStatus
            | undefined;

          if (status === "backpressure") {
            toast.error("Refresh failed", {
              description: "Service is busy. Please try again in a moment.",
            });
            return;
          }

          const newJobId = data?.job_id;
          if (newJobId) {
            setJobId(newJobId);
            setIsRefreshing(true);
          }

          toast.info(
            status === "already_running"
              ? "Refresh already running"
              : "Refresh queued",
            {
              description: customersOnly
                ? status === "already_running"
                  ? "A refresh is already in progress."
                  : "Customer data is updating in the background."
                : status === "already_running"
                  ? "A refresh is already in progress."
                  : "Invoice data is updating in the background. This usually takes a few minutes.",
            },
          );
        },
        onError: (error: unknown) => {
          completionHandledRef.current = true;
          setIsRefreshing(false);
          const err = error as {
            response?: { data?: { detail?: string } };
          };
          const detail = err?.response?.data?.detail;
          toast.error("Refresh failed", {
            description:
              typeof detail === "string" && detail.length > 0
                ? detail
                : customersOnly
                  ? "Could not start customer sync. Please try again."
                  : "Could not start invoice refresh. Please try again.",
          });
        },
      });
    },
    [refreshCustomersMutation, refreshInvoicesMutation],
  );

  const value = useMemo(
    () => ({
      organization,
      refreshInvoicesMutation,
      refreshCustomersMutation,
      isRefreshing,
      startRefresh,
    }),
    [
      organization,
      refreshInvoicesMutation,
      refreshCustomersMutation,
      isRefreshing,
      startRefresh,
    ],
  );

  return (
    <AccountingRefreshContext.Provider value={value}>
      {children}
    </AccountingRefreshContext.Provider>
  );
}

export function useAsyncAccountingRefresh(): AccountingRefreshContextValue {
  const context = useContext(AccountingRefreshContext);
  if (!context) {
    throw new Error(
      "useAsyncAccountingRefresh must be used within AccountingRefreshProvider",
    );
  }
  return context;
}
